import { Image } from '@graphcommerce/image'
import { ConfigurableOptionValueImageFragment } from '@graphcommerce/magento-product-configurable'
import { ActionCardItemRenderProps, ActionCard, responsiveVal } from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Box, Button, Popper } from '@mui/material'
import { useState } from 'react'

export type ConfigurableOptionValueImageProps =
  ActionCardItemRenderProps<ConfigurableOptionValueImageFragment>

export function ConfigurableOptionValueImage(props: ConfigurableOptionValueImageProps) {
  const {
    swatch_data,
    store_label,
    uid,
    use_default_value,
    size = 'large',
    selected,
    onClick,
    onReset,
    value,
    disabled,
    ...actionCardProps
  } = props

  if (swatch_data?.__typename !== 'ImageSwatchData')
    throw Error(`ConfigurableOptionValueImage can not render a ${swatch_data?.__typename}`)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const openPopper = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closePopper = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popper' : undefined

  return (
    <>
      <Box
        onMouseEnter={openPopper}
        onMouseLeave={closePopper}
        sx={
          disabled
            ? {
                position: 'relative',
                '&:after': {
                  content: '" "',
                  width: responsiveVal(35, 45),
                  height: '3px',
                  display: 'block',
                  background: 'rgba(0,0,0,0.2)',
                  position: 'absolute',
                  top: '50%',
                  boxShadow: '0 0 3px 1px rgba(255,255,255,0.6)',
                  transform: 'rotate(-45deg)',
                  pointerEvents: 'none',
                },
              }
            : {}
        }
      >
        <ActionCard
          {...actionCardProps}
          sx={disabled ? { opacity: 0.33 } : {}}
          image={
            swatch_data.thumbnail && (
              <Image
                dontReportWronglySizedImages
                src={swatch_data.thumbnail ?? ''}
                layout='fill'
                alt={swatch_data.value ?? ''}
                sizes={responsiveVal(35, 45)}
                sx={{
                  display: 'block',
                  borderRadius: '50%',
                  width: responsiveVal(35, 45),
                  height: responsiveVal(35, 45),
                  objectFit: 'cover',
                }}
              />
            )
          }
          title={store_label}
          selected={selected}
          value={value}
          onClick={
            onClick &&
            ((e) => {
              if (selected) {
                onReset(e)
              } else {
                onClick(e, value)
              }
            })
          }
          reset={
            <Button variant='inline' color='secondary' onClick={onReset} disableRipple>
              <Trans id='Change' />
            </Button>
          }
          size={size}
        />
      </Box>
      {anchorEl && (
        <Popper
          placement='top'
          id={id}
          open={open}
          anchorEl={anchorEl}
          sx={(theme) => ({
            zIndex: 999,
            '& .popper-wrapper': {
              border: `2px solid #d9d9d9`,
              borderRadius: theme.shape.borderRadius,
              p: theme.spacings.xs,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              gap: `10px`,
              alignItems: 'center',
              position: 'relative',
              mb: '15px',
              '&:after,&:before': {
                top: '100%',
                left: '50%',
                border: 'solid transparent',
                content: '""',
                height: 0,
                width: 0,
                position: 'absolute',
                pointerEvents: 'none',
              },
              '&:after': {
                borderColor: 'transparent',
                borderTopColor: 'white',
                borderWidth: '10px',
                marginLeft: '-10px',
              },
              '&:before': {
                borderColor: 'rgba(0, 0, 0, 0)',
                borderTopColor: '#d9d9d9',
                borderWidth: '13px',
                marginLeft: '-13px',
              },
            },
            '&[data-popper-placement="bottom"] .popper-wrapper': {
              '&:after,&:before': {
                transform: 'rotate(180deg)',
                top: 'unset',
                bottom: '100%',
              },
            },
          })}
        >
          <Box className='popper-wrapper'>
            <Box sx={{ width: 120, height: 120 }}>
              <Image
                dontReportWronglySizedImages
                src={swatch_data.thumbnail ?? ''}
                layout='fill'
                alt={swatch_data.value ?? ''}
                sizes='120px'
                sx={{
                  display: 'block',
                  borderRadius: '50%',
                  width: 120,
                  height: 120,
                  objectFit: 'cover',
                }}
              />
            </Box>
            {store_label
              ?.replaceAll('_', ' ')
              .split(' ')
              .map((word) => word[0].toUpperCase() + word.substring(1))
              .join(' ')}
          </Box>
        </Popper>
      )}
    </>
  )
}
